import { useState } from "react";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { MessageSquareReply } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";

/**
 * ReviewReplyDialog — lets a handyman post a single public reply to one of their reviews.
 */
export function ReviewReplyDialog({ review, onReplied }) {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reply.trim() || submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/reviews/${review.review_id}/reply`, { reply: reply.trim() });
      toast.success("Reply posted");
      setOpen(false);
      onReplied?.(review.review_id, r.data.reply, r.data.reply_at);
    } catch (e) {
      Sentry.captureException(e);
      const msg = e.response?.data?.detail || "Could not post reply";
      toast.error(typeof msg === "string" ? msg : "Could not post reply");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full border-terracotta text-terracotta hover:bg-terracotta/10 mt-3"
          data-testid={`reply-open-${review.review_id}`}
        >
          <MessageSquareReply className="w-4 h-4 mr-1" /> Reply to this review
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`reply-dialog-${review.review_id}`}>
        <DialogHeader>
          <DialogTitle>Reply to {review.student_name}</DialogTitle>
          <DialogDescription>
            Your reply is public and visible to anyone who views your profile. Keep it professional — this is a chance
            to show future students how you handle feedback.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-cream/30 rounded-lg p-3 text-sm text-charcoal/75 border border-charcoal/10">
          <p className="font-medium mb-1">{review.student_name} · {review.rating}★</p>
          <p>“{review.comment}”</p>
        </div>

        <Textarea
          placeholder="Thanks for the feedback! Here's what happened…"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={4}
          maxLength={800}
          data-testid={`reply-textarea-${review.review_id}`}
        />
        <p className="text-xs text-charcoal/40 text-right">{reply.length} / 800</p>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid={`reply-cancel-${review.review_id}`}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !reply.trim()}
            data-testid={`reply-submit-${review.review_id}`}
            className="bg-terracotta hover:bg-terracotta/90 text-white"
          >
            {submitting ? "Posting…" : "Post reply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
